import {
  faDownload,
  faPencil,
  faUpload,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Badge,
  Button,
  Grid,
  Icon,
  Message,
  Modal,
  TextArea,
} from "@intility/bifrost-react";
import Box from "@intility/bifrost-react/Box";
import { useRef, useState } from "react";
import {
  planBlueprintManifest,
  useApplyBlueprintManifest,
  useBlueprintManifestExport,
} from "../../api/blueprintManifests";
import type {
  BlueprintManifest,
  BlueprintManifestRefusal,
  BlueprintManifestResult,
} from "../../api/types";
import { CopyButton } from "../../components/CopyButton";
import {
  downloadTextFile,
  editorRows,
  editorStyle,
  ManifestParseError,
  manifestToYaml,
  yamlToManifest,
} from "../../lib/manifestFile";

const OUTCOME_LABELS: Record<string, string> = {
  created: "Will create",
  linked: "Will link",
  unchanged: "No changes",
  refused: "Refused",
  untouched: "Left as it is",
};

// After an apply the same words read in the past tense.
const APPLIED_LABELS: Record<string, string> = {
  created: "Created",
  linked: "Linked",
  unchanged: "No changes",
  refused: "Refused",
  untouched: "Left as it is",
};

function OutcomeBadge({
  outcome,
  applied,
}: {
  outcome: string;
  applied: boolean;
}) {
  const labels = applied ? APPLIED_LABELS : OUTCOME_LABELS;
  const state =
    outcome === "created" || outcome === "linked"
      ? "success"
      : outcome === "refused"
        ? "alert"
        : "neutral";
  return <Badge state={state}>{labels[outcome] ?? outcome}</Badge>;
}

function Refusal({ refusal }: { refusal?: BlueprintManifestRefusal | null }) {
  if (!refusal) return null;
  return (
    <p className="bf-p" style={{ margin: 0 }}>
      {refusal.message} <code>{refusal.code}</code>
    </p>
  );
}

function OutcomeRow({
  title,
  outcome,
  applied,
  refusal,
  detail,
}: {
  title: string;
  outcome: string;
  applied: boolean;
  refusal?: BlueprintManifestRefusal | null;
  detail?: string;
}) {
  return (
    <Box border radius padding={16}>
      <Grid gap={8}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <code>{title}</code>
          <OutcomeBadge outcome={outcome} applied={applied} />
        </div>
        {detail && (
          <p className="bf-p" style={{ margin: 0 }}>
            {detail}
          </p>
        )}
        <Refusal refusal={refusal} />
      </Grid>
    </Box>
  );
}

// Per-object outcomes of a plan or an apply: the platform, the resource types,
// then the placements. Placements the manifest does not name show as left alone.
export function BlueprintManifestOutcomes({
  result,
  applied,
}: {
  result: BlueprintManifestResult;
  applied: boolean;
}) {
  return (
    <Grid gap={12}>
      <OutcomeRow
        title={`Platform ${result.platform.slug}`}
        outcome={result.platform.outcome}
        applied={applied}
        refusal={result.platform.refusal}
      />
      {result.resourceTypes.length > 0 && (
        <h5 className="bf-h5" style={{ margin: 0 }}>
          Resource types
        </h5>
      )}
      {result.resourceTypes.map((t) => (
        <OutcomeRow
          key={t.slug}
          title={t.slug}
          outcome={t.outcome}
          applied={applied}
          refusal={t.refusal}
          detail={
            t.differingFields && t.differingFields.length > 0
              ? `Differs from the existing type in: ${t.differingFields.join(", ")}`
              : undefined
          }
        />
      ))}
      {result.placements.length > 0 && (
        <h5 className="bf-h5" style={{ margin: 0 }}>
          Placements
        </h5>
      )}
      {result.placements.map((p) => (
        <OutcomeRow
          key={`${p.qualifiedName}-${p.type}`}
          title={p.qualifiedName}
          outcome={p.outcome}
          applied={applied}
          refusal={p.refusal}
          detail={`Type ${p.type}${p.parent ? `, under ${p.parent}` : ""}`}
        />
      ))}
    </Grid>
  );
}

function errorText(err: unknown) {
  return err instanceof Error ? err.message : "Something went wrong.";
}

export function BlueprintManifestTab({
  platformSlug,
  canEdit,
}: {
  platformSlug: string;
  canEdit: boolean;
}) {
  const exported = useBlueprintManifestExport(platformSlug);
  const apply = useApplyBlueprintManifest();
  const fileInput = useRef<HTMLInputElement>(null);

  const [text, setText] = useState("");
  const [parsed, setParsed] = useState<BlueprintManifest | null>(null);
  const [plan, setPlan] = useState<BlueprintManifestResult | null>(null);
  const [applied, setApplied] = useState<BlueprintManifestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlanning, setIsPlanning] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [editing, setEditing] = useState(false);

  const currentYaml = exported.data ? manifestToYaml(exported.data) : "";
  const hasChanges =
    plan != null &&
    (plan.platform.outcome === "created" ||
      plan.resourceTypes.some((t) => t.outcome === "created") ||
      plan.placements.some((p) => p.outcome === "created"));

  function handleTextChange(value: string) {
    setText(value);
    setParsed(null);
    setPlan(null);
    setApplied(null);
    setError(null);
  }

  function handleStartEdit() {
    handleTextChange(currentYaml);
    setEditing(true);
  }

  function handleCancelEdit() {
    handleTextChange("");
    setEditing(false);
  }

  async function handleFile(file: File | undefined) {
    if (!file) return;
    handleTextChange(await file.text());
    if (fileInput.current) fileInput.current.value = "";
  }

  async function handlePreview() {
    setError(null);
    setApplied(null);
    setIsPlanning(true);
    try {
      const manifest = yamlToManifest<BlueprintManifest>(text);
      if (manifest.platform?.slug !== platformSlug) {
        throw new ManifestParseError(
          `This manifest is for platform "${manifest.platform?.slug ?? ""}", but this page is for "${platformSlug}". Open the other platform to apply it.`,
        );
      }
      setParsed(manifest);
      setPlan(await planBlueprintManifest(manifest));
    } catch (err) {
      setPlan(null);
      setError(
        err instanceof ManifestParseError ? err.message : errorText(err),
      );
    } finally {
      setIsPlanning(false);
    }
  }

  function handleApply() {
    if (!parsed) return;
    apply.mutate(parsed, {
      onSuccess: (result) => {
        setApplied(result);
        setPlan(null);
        setParsed(null);
        setShowConfirm(false);
        setEditing(false);
      },
    });
  }

  return (
    <Grid gap={24}>
      <Grid gap={8}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h3 className="bf-h3" style={{ margin: 0, marginRight: "auto" }}>
            Blueprint manifest
          </h3>
          {canEdit && !editing && (
            <Button
              variant="flat"
              small
              state={currentYaml ? undefined : "inactive"}
              onClick={() => currentYaml && handleStartEdit()}
            >
              <Icon icon={faPencil} marginRight />
              Edit
            </Button>
          )}
          <CopyButton value={currentYaml} />
          <Button
            variant="flat"
            small
            state={currentYaml ? undefined : "inactive"}
            onClick={() =>
              currentYaml &&
              downloadTextFile(`${platformSlug}.yaml`, currentYaml)
            }
          >
            <Icon icon={faDownload} marginRight />
            Download
          </Button>
        </div>
        <p className="bf-p">
          This is the live blueprint as a manifest. Use it to copy this
          blueprint to another environment. It never holds access or company
          availability.
        </p>
        {exported.isLoading && <p className="bf-p">Loading…</p>}
        {exported.error && (
          <Message state="alert" header="Could not load the manifest">
            {errorText(exported.error)}
          </Message>
        )}
        {!editing && currentYaml && (
          <Box border radius padding={16}>
            <pre style={{ margin: 0, overflow: "auto" }}>{currentYaml}</pre>
          </Box>
        )}
      </Grid>

      {canEdit && editing && (
        <Grid gap={12}>
          <p className="bf-p">
            Edit the manifest or upload a file. Preview shows what would change.
            Nothing changes until you confirm. An apply only adds: it never
            changes or deletes what already exists.
          </p>
          <TextArea
            label="Manifest"
            value={text}
            rows={editorRows(text)}
            textAreaStyle={editorStyle}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder="apiVersion: id.intility.com/v1"
          />
          <div style={{ display: "flex", gap: 8 }}>
            <input
              ref={fileInput}
              type="file"
              accept=".yaml,.yml,text/yaml"
              hidden
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Button
              variant="filled"
              onClick={handlePreview}
              state={isPlanning || !text.trim() ? "inactive" : undefined}
            >
              {isPlanning ? "Checking…" : "Preview changes"}
            </Button>
            <Button variant="basic" onClick={() => fileInput.current?.click()}>
              <Icon icon={faUpload} marginRight />
              Upload file
            </Button>
            <Button variant="flat" onClick={handleCancelEdit}>
              Cancel
            </Button>
          </div>
          {error && (
            <Message state="alert" header="Could not preview the manifest">
              {error}
            </Message>
          )}
          {plan && (
            <Grid gap={12}>
              <h4 className="bf-h4" style={{ margin: 0 }}>
                Preview
              </h4>
              <BlueprintManifestOutcomes result={plan} applied={false} />
              <div>
                <Button
                  variant="filled"
                  state={hasChanges ? undefined : "inactive"}
                  onClick={() => hasChanges && setShowConfirm(true)}
                >
                  Save changes
                </Button>
              </div>
            </Grid>
          )}
        </Grid>
      )}
      {applied && (
        <Grid gap={12}>
          <h4 className="bf-h4" style={{ margin: 0 }}>
            Result
          </h4>
          <BlueprintManifestOutcomes result={applied} applied />
        </Grid>
      )}

      <Modal
        isOpen={showConfirm}
        onRequestClose={() => {
          if (apply.isPending) return;
          setShowConfirm(false);
          apply.reset();
        }}
        header="Save blueprint changes"
        width={480}
      >
        <Grid gap={16}>
          <p className="bf-p" style={{ margin: 0 }}>
            This adds the new resource types and placements shown in the preview
            to this environment. Objects that are refused are skipped.
          </p>
          {apply.error && (
            <Message state="alert" header="Save failed">
              {errorText(apply.error)}
            </Message>
          )}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button
              variant="flat"
              state={apply.isPending ? "inactive" : undefined}
              onClick={
                apply.isPending
                  ? undefined
                  : () => {
                      setShowConfirm(false);
                      apply.reset();
                    }
              }
            >
              Cancel
            </Button>
            <Button
              variant="filled"
              state={apply.isPending ? "inactive" : undefined}
              onClick={apply.isPending ? undefined : handleApply}
            >
              {apply.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </Grid>
      </Modal>
    </Grid>
  );
}
